import React, { useState } from 'react';

const DocumentForm = () => {
  const [activeTab, setActiveTab] = useState('document');
  const [formData, setFormData] = useState({
    // Document-level Metadata
    doc_id: '',
    doc_title: '',
    doc_category: '',
    doc_type: '',
    audience: '',
    trust_level: '',
    industry_domain: '',
    jurisdiction: '',
    languages: [],
    classification: '',
    confidence: '',
    reasoning: '',
    source_system: '',
    source_uri: '',
    source_format: '',
    canonical_version: '',
    doc_checksum: '',
    created_at: new Date().toISOString().slice(0, 16),
    updated_at: new Date().toISOString().slice(0, 16),
    owner: '',
    retention_policy: '',
    
    // Lifecycle & Versioning
    status: 'active',
    expiry_date: '',
    review_cycle: '',
    last_reviewed_at: '',
    supersedes: '',
    superseded_by: '',
    soft_delete_at: '',
    hard_delete_at: '',
    
    // Chunk-level Metadata (for first chunk)
    chunks: [{
      chunk_id: '',
      parent_doc_id: '',
      chunk_index: 0,
      hierarchy_path: '',
      section_title: '',
      previous_chunk_id: '',
      next_chunk_id: '',
      token_count: '',
      language: '',
      effective_classification: '',
      effective_sensitivity_reason: '',
      keywords: [],
      summary: '',
      cross_refs: [],
      source_span: { start: '', end: '', page_from: '', page_to: '' }
    }],
    
    // Processing / Pipeline Metadata
    ingestion_run_id: '',
    preprocessing_ops: [],
    enrichment_ops: [],
    chunking_strategy: { type: '', version: '', params: {} },
    qa_checks: [],
    
    // Retrieval / Embedding Metadata
    embedding_model: '',
    embedding_version: '',
    embedding_vector_id: '',
    embedding_created_at: '',
    index_name: '',
    index_filters: {},
    similarity_score_last: '',
    
    // Security / Compliance / Audit
    pii_detected: false,
    regulatory_tags: [],
    rbac_tags: [],
    access_log_ref: '',
    audit_trail: [],
    
    // Optional Enrichment
    glossary_links: [],
    topic_modeling: [],
    graph_edges: []
  });

  const [errors, setErrors] = useState({});
  const [submitResult, setSubmitResult] = useState(null);

  const tabs = [
    { id: 'document', label: 'Document Metadata' },
    { id: 'chunk', label: 'Chunk Metadata' },
    { id: 'processing', label: 'Processing' },
    { id: 'embedding', label: 'Embedding' },
    { id: 'security', label: 'Security & Compliance' },
    { id: 'lifecycle', label: 'Lifecycle' },
    { id: 'enrichment', label: 'Enrichment' }
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleArrayChange = (field, index, value) => {
    setFormData(prev => {
      const newArray = [...prev[field]];
      newArray[index] = value;
      return { ...prev, [field]: newArray };
    });
  };

  const addArrayItem = (field, defaultValue = '') => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], defaultValue]
    }));
  };

  const removeArrayItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleChunkChange = (index, field, value) => {
    setFormData(prev => {
      const newChunks = [...prev.chunks];
      newChunks[index] = { ...newChunks[index], [field]: value };
      return { ...prev, chunks: newChunks };
    });
  };

  const handleChunkArrayChange = (chunkIndex, field, itemIndex, value) => {
    setFormData(prev => {
      const newChunks = [...prev.chunks];
      const newArray = [...newChunks[chunkIndex][field]];
      newArray[itemIndex] = value;
      newChunks[chunkIndex] = { ...newChunks[chunkIndex], [field]: newArray };
      return { ...prev, chunks: newChunks };
    });
  };

  const handleChunkSourceSpanChange = (chunkIndex, field, value) => {
    setFormData(prev => {
      const newChunks = [...prev.chunks];
      newChunks[chunkIndex] = {
        ...newChunks[chunkIndex],
        source_span: { ...newChunks[chunkIndex].source_span, [field]: value }
      };
      return { ...prev, chunks: newChunks };
    });
  };

  const validate = () => {
    const newErrors = {};
    
    // Required fields
    if (!formData.doc_id) newErrors.doc_id = 'Document ID is required';
    if (!formData.classification) newErrors.classification = 'Classification is required';
    if (!formData.source_system) newErrors.source_system = 'Source system is required';
    if (!formData.source_format) newErrors.source_format = 'Source format is required';
    if (!formData.canonical_version) newErrors.canonical_version = 'Canonical version is required';
    if (!formData.status) newErrors.status = 'Status is required';
    
    // Validate chunks
    formData.chunks.forEach((chunk, idx) => {
      if (!chunk.chunk_id) newErrors[`chunk_${idx}_chunk_id`] = 'Chunk ID is required';
      if (!chunk.token_count) newErrors[`chunk_${idx}_token_count`] = 'Token count is required';
      if (!chunk.effective_classification) newErrors[`chunk_${idx}_effective_classification`] = 'Effective classification is required';
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) {
      setSubmitResult({ type: 'error', message: 'Please fix the errors before submitting' });
      return;
    }

    // Auto-fill some fields
    const submitData = {
      ...formData,
      created_at: formData.created_at ? new Date(formData.created_at).toISOString() : new Date().toISOString(),
      updated_at: formData.updated_at ? new Date(formData.updated_at).toISOString() : new Date().toISOString(),
      chunks: formData.chunks.map(chunk => ({
        ...chunk,
        parent_doc_id: chunk.parent_doc_id || formData.doc_id,
        chunk_index: chunk.chunk_index || 0,
        token_count: parseInt(chunk.token_count) || 0,
        source_span: {
          ...chunk.source_span,
          start: chunk.source_span.start ? parseInt(chunk.source_span.start) : undefined,
          end: chunk.source_span.end ? parseInt(chunk.source_span.end) : undefined,
          page_from: chunk.source_span.page_from ? parseInt(chunk.source_span.page_from) : undefined,
          page_to: chunk.source_span.page_to ? parseInt(chunk.source_span.page_to) : undefined
        }
      }))
    };

    // Remove empty optional fields
    Object.keys(submitData).forEach(key => {
      if (submitData[key] === '' || (Array.isArray(submitData[key]) && submitData[key].length === 0)) {
        if (!['chunks', 'preprocessing_ops', 'enrichment_ops', 'qa_checks', 'regulatory_tags', 'rbac_tags', 'audit_trail', 'glossary_links', 'topic_modeling', 'graph_edges', 'cross_refs', 'keywords', 'languages'].includes(key)) {
          delete submitData[key];
        }
      }
    });

    setSubmitResult({
      type: 'success',
      message: 'Form submitted successfully!',
      data: submitData
    });

    // Scroll to result
    setTimeout(() => {
      document.querySelector('.submit-result')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleReset = () => {
    setFormData({
      doc_id: '',
      doc_title: '',
      doc_category: '',
      doc_type: '',
      audience: '',
      trust_level: '',
      industry_domain: '',
      jurisdiction: '',
      languages: [],
      classification: '',
      confidence: '',
      reasoning: '',
      source_system: '',
      source_uri: '',
      source_format: '',
      canonical_version: '',
      doc_checksum: '',
      created_at: new Date().toISOString().slice(0, 16),
      updated_at: new Date().toISOString().slice(0, 16),
      owner: '',
      retention_policy: '',
      status: 'active',
      expiry_date: '',
      review_cycle: '',
      last_reviewed_at: '',
      supersedes: '',
      superseded_by: '',
      soft_delete_at: '',
      hard_delete_at: '',
      chunks: [{
        chunk_id: '',
        parent_doc_id: '',
        chunk_index: 0,
        hierarchy_path: '',
        section_title: '',
        previous_chunk_id: '',
        next_chunk_id: '',
        token_count: '',
        language: '',
        effective_classification: '',
        effective_sensitivity_reason: '',
        keywords: [],
        summary: '',
        cross_refs: [],
        source_span: { start: '', end: '', page_from: '', page_to: '' }
      }],
      ingestion_run_id: '',
      preprocessing_ops: [],
      enrichment_ops: [],
      chunking_strategy: { type: '', version: '', params: {} },
      qa_checks: [],
      embedding_model: '',
      embedding_version: '',
      embedding_vector_id: '',
      embedding_created_at: '',
      index_name: '',
      index_filters: {},
      similarity_score_last: '',
      pii_detected: false,
      regulatory_tags: [],
      rbac_tags: [],
      access_log_ref: '',
      audit_trail: [],
      glossary_links: [],
      topic_modeling: [],
      graph_edges: []
    });
    setErrors({});
    setSubmitResult(null);
  };

  const renderArrayInput = (field, label, placeholder, required = false) => (
    <div className="form-group full-width">
      <label className="form-label">
        {label}
        {required && <span className="required">*</span>}
      </label>
      {formData[field].map((item, index) => (
        <div key={index} className="array-item">
          <input
            type="text"
            value={item}
            onChange={(e) => handleArrayChange(field, index, e.target.value)}
            placeholder={placeholder}
            className="form-input"
          />
          <button
            type="button"
            onClick={() => removeArrayItem(field, index)}
            className="btn-remove"
          >
            Remove
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => addArrayItem(field)}
        className="btn-add"
      >
        + Add {label}
      </button>
    </div>
  );

  return (
    <div className="form-container">
      <div className="form-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            type="button"
            className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <form onSubmit={handleSubmit}>
        {/* Document-level Metadata */}
        <div className={`form-section ${activeTab === 'document' ? 'active' : ''}`}>
          <h2 className="section-title">Document-level Metadata</h2>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">
                Document ID <span className="required">*</span>
              </label>
              <input
                type="text"
                name="doc_id"
                value={formData.doc_id}
                onChange={handleChange}
                placeholder="doc_7f9c2a"
                className={`form-input ${errors.doc_id ? 'error' : ''}`}
              />
              {errors.doc_id && <div className="error-message">{errors.doc_id}</div>}
              <div className="help-text">Unique document ID (UUID or primary key)</div>
            </div>

            <div className="form-group">
              <label className="form-label">
                Document Title
                <span className="optional">(Recommended)</span>
              </label>
              <input
                type="text"
                name="doc_title"
                value={formData.doc_title}
                onChange={handleChange}
                placeholder="Employee Leave Policy"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Document Category
                <span className="optional">(Recommended)</span>
              </label>
              <input
                type="text"
                name="doc_category"
                value={formData.doc_category}
                onChange={handleChange}
                placeholder="HR"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Document Type
                <span className="optional">(Recommended)</span>
              </label>
              <input
                type="text"
                name="doc_type"
                value={formData.doc_type}
                onChange={handleChange}
                placeholder="Policy"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Audience
                <span className="optional">(Recommended)</span>
              </label>
              <select
                name="audience"
                value={formData.audience}
                onChange={handleChange}
                className="form-select"
              >
                <option value="">Select audience</option>
                <option value="external">External</option>
                <option value="internal">Internal</option>
                <option value="mixed">Mixed</option>
                <option value="regulated">Regulated</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">
                Trust Level
                <span className="optional">(Recommended)</span>
              </label>
              <select
                name="trust_level"
                value={formData.trust_level}
                onChange={handleChange}
                className="form-select"
              >
                <option value="">Select trust level</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">
                Industry Domain
                <span className="optional">(Optional)</span>
              </label>
              <select
                name="industry_domain"
                value={formData.industry_domain}
                onChange={handleChange}
                className="form-select"
              >
                <option value="">Select domain</option>
                <option value="Finance">Finance</option>
                <option value="Tech">Tech</option>
                <option value="Healthcare">Healthcare</option>
                <option value="Legal">Legal</option>
                <option value="General">General</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">
                Jurisdiction
                <span className="optional">(Optional)</span>
              </label>
              <select
                name="jurisdiction"
                value={formData.jurisdiction}
                onChange={handleChange}
                className="form-select"
              >
                <option value="">Select jurisdiction</option>
                <option value="US">US</option>
                <option value="EU">EU</option>
                <option value="UK">UK</option>
                <option value="Global">Global</option>
                <option value="Unknown">Unknown</option>
              </select>
            </div>

            {renderArrayInput('languages', 'Languages', 'e.g., en, zh', false)}

            <div className="form-group">
              <label className="form-label">
                Classification <span className="required">*</span>
              </label>
              <select
                name="classification"
                value={formData.classification}
                onChange={handleChange}
                className={`form-select ${errors.classification ? 'error' : ''}`}
              >
                <option value="">Select classification</option>
                <option value="public">Public</option>
                <option value="internal">Internal</option>
                <option value="confidential">Confidential</option>
                <option value="restricted">Restricted</option>
              </select>
              {errors.classification && <div className="error-message">{errors.classification}</div>}
            </div>

            <div className="form-group">
              <label className="form-label">
                Confidence
                <span className="optional">(Recommended)</span>
              </label>
              <select
                name="confidence"
                value={formData.confidence}
                onChange={handleChange}
                className="form-select"
              >
                <option value="">Select confidence</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>

            <div className="form-group full-width">
              <label className="form-label">
                Reasoning
                <span className="optional">(Optional)</span>
              </label>
              <textarea
                name="reasoning"
                value={formData.reasoning}
                onChange={handleChange}
                placeholder="Internal procedures; no PII"
                className="form-textarea"
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Source System <span className="required">*</span>
              </label>
              <input
                type="text"
                name="source_system"
                value={formData.source_system}
                onChange={handleChange}
                placeholder="SharePoint"
                className={`form-input ${errors.source_system ? 'error' : ''}`}
              />
              {errors.source_system && <div className="error-message">{errors.source_system}</div>}
            </div>

            <div className="form-group">
              <label className="form-label">
                Source URI
                <span className="optional">(Recommended)</span>
              </label>
              <input
                type="text"
                name="source_uri"
                value={formData.source_uri}
                onChange={handleChange}
                placeholder="/sites/hr/policies/leave-policy.pdf"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Source Format <span className="required">*</span>
              </label>
              <select
                name="source_format"
                value={formData.source_format}
                onChange={handleChange}
                className={`form-select ${errors.source_format ? 'error' : ''}`}
              >
                <option value="">Select format</option>
                <option value="pdf">PDF</option>
                <option value="docx">DOCX</option>
                <option value="html">HTML</option>
                <option value="md">Markdown</option>
                <option value="txt">TXT</option>
                <option value="pptx">PPTX</option>
              </select>
              {errors.source_format && <div className="error-message">{errors.source_format}</div>}
            </div>

            <div className="form-group">
              <label className="form-label">
                Canonical Version <span className="required">*</span>
              </label>
              <input
                type="text"
                name="canonical_version"
                value={formData.canonical_version}
                onChange={handleChange}
                placeholder="3.1"
                className={`form-input ${errors.canonical_version ? 'error' : ''}`}
              />
              {errors.canonical_version && <div className="error-message">{errors.canonical_version}</div>}
            </div>

            <div className="form-group">
              <label className="form-label">
                Document Checksum
                <span className="optional">(Recommended)</span>
              </label>
              <input
                type="text"
                name="doc_checksum"
                value={formData.doc_checksum}
                onChange={handleChange}
                placeholder="sha256:ab12..."
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Created At <span className="required">*</span>
              </label>
              <input
                type="datetime-local"
                name="created_at"
                value={formData.created_at}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Updated At <span className="required">*</span>
              </label>
              <input
                type="datetime-local"
                name="updated_at"
                value={formData.updated_at}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Owner
                <span className="optional">(Recommended)</span>
              </label>
              <input
                type="text"
                name="owner"
                value={formData.owner}
                onChange={handleChange}
                placeholder="HR Operations"
                className="form-input"
              />
            </div>

            <div className="form-group full-width">
              <label className="form-label">
                Retention Policy
                <span className="optional">(Recommended)</span>
              </label>
              <textarea
                name="retention_policy"
                value={formData.retention_policy}
                onChange={handleChange}
                placeholder="retain 7 years; archive after 2 years"
                className="form-textarea"
              />
            </div>
          </div>
        </div>

        {/* Chunk-level Metadata */}
        <div className={`form-section ${activeTab === 'chunk' ? 'active' : ''}`}>
          <h2 className="section-title">Chunk-level Metadata</h2>
          {formData.chunks.map((chunk, chunkIndex) => (
            <div key={chunkIndex} style={{ marginBottom: '30px', padding: '20px', background: '#f8f9fa', borderRadius: '8px' }}>
              <h3 style={{ marginBottom: '15px' }}>Chunk {chunkIndex + 1}</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">
                    Chunk ID <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    value={chunk.chunk_id}
                    onChange={(e) => handleChunkChange(chunkIndex, 'chunk_id', e.target.value)}
                    placeholder="chunk_3a9f..."
                    className={`form-input ${errors[`chunk_${chunkIndex}_chunk_id`] ? 'error' : ''}`}
                  />
                  {errors[`chunk_${chunkIndex}_chunk_id`] && <div className="error-message">{errors[`chunk_${chunkIndex}_chunk_id`]}</div>}
                </div>

                <div className="form-group">
                  <label className="form-label">Parent Document ID</label>
                  <input
                    type="text"
                    value={chunk.parent_doc_id || formData.doc_id}
                    onChange={(e) => handleChunkChange(chunkIndex, 'parent_doc_id', e.target.value)}
                    placeholder={formData.doc_id || "doc_7f9c2a"}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Chunk Index</label>
                  <input
                    type="number"
                    value={chunk.chunk_index}
                    onChange={(e) => handleChunkChange(chunkIndex, 'chunk_index', parseInt(e.target.value) || 0)}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Hierarchy Path</label>
                  <input
                    type="text"
                    value={chunk.hierarchy_path}
                    onChange={(e) => handleChunkChange(chunkIndex, 'hierarchy_path', e.target.value)}
                    placeholder="/Policy/Scope/Eligibility"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Section Title</label>
                  <input
                    type="text"
                    value={chunk.section_title}
                    onChange={(e) => handleChunkChange(chunkIndex, 'section_title', e.target.value)}
                    placeholder="Eligibility"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Token Count <span className="required">*</span></label>
                  <input
                    type="number"
                    value={chunk.token_count}
                    onChange={(e) => handleChunkChange(chunkIndex, 'token_count', e.target.value)}
                    placeholder="420"
                    className={`form-input ${errors[`chunk_${chunkIndex}_token_count`] ? 'error' : ''}`}
                  />
                  {errors[`chunk_${chunkIndex}_token_count`] && <div className="error-message">{errors[`chunk_${chunkIndex}_token_count`]}</div>}
                </div>

                <div className="form-group">
                  <label className="form-label">Language</label>
                  <input
                    type="text"
                    value={chunk.language}
                    onChange={(e) => handleChunkChange(chunkIndex, 'language', e.target.value)}
                    placeholder="en"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Effective Classification <span className="required">*</span>
                  </label>
                  <select
                    value={chunk.effective_classification}
                    onChange={(e) => handleChunkChange(chunkIndex, 'effective_classification', e.target.value)}
                    className={`form-select ${errors[`chunk_${chunkIndex}_effective_classification`] ? 'error' : ''}`}
                  >
                    <option value="">Select classification</option>
                    <option value="public">Public</option>
                    <option value="internal">Internal</option>
                    <option value="confidential">Confidential</option>
                    <option value="restricted">Restricted</option>
                  </select>
                  {errors[`chunk_${chunkIndex}_effective_classification`] && <div className="error-message">{errors[`chunk_${chunkIndex}_effective_classification`]}</div>}
                </div>

                <div className="form-group full-width">
                  <label className="form-label">Effective Sensitivity Reason</label>
                  <textarea
                    value={chunk.effective_sensitivity_reason}
                    onChange={(e) => handleChunkChange(chunkIndex, 'effective_sensitivity_reason', e.target.value)}
                    placeholder="Contains employee IDs"
                    className="form-textarea"
                  />
                </div>

                <div className="form-group full-width">
                  <label className="form-label">Keywords</label>
                  {chunk.keywords.map((keyword, idx) => (
                    <div key={idx} className="array-item">
                      <input
                        type="text"
                        value={keyword}
                        onChange={(e) => handleChunkArrayChange(chunkIndex, 'keywords', idx, e.target.value)}
                        placeholder="annual leave"
                        className="form-input"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const newKeywords = chunk.keywords.filter((_, i) => i !== idx);
                          handleChunkChange(chunkIndex, 'keywords', newKeywords);
                        }}
                        className="btn-remove"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      handleChunkChange(chunkIndex, 'keywords', [...chunk.keywords, '']);
                    }}
                    className="btn-add"
                  >
                    + Add Keyword
                  </button>
                </div>

                <div className="form-group full-width">
                  <label className="form-label">Summary</label>
                  <textarea
                    value={chunk.summary}
                    onChange={(e) => handleChunkChange(chunkIndex, 'summary', e.target.value)}
                    placeholder="Defines eligibility conditions for annual leave"
                    className="form-textarea"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Source Span - Start</label>
                  <input
                    type="number"
                    value={chunk.source_span.start}
                    onChange={(e) => handleChunkSourceSpanChange(chunkIndex, 'start', e.target.value)}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Source Span - End</label>
                  <input
                    type="number"
                    value={chunk.source_span.end}
                    onChange={(e) => handleChunkSourceSpanChange(chunkIndex, 'end', e.target.value)}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Source Span - Page From</label>
                  <input
                    type="number"
                    value={chunk.source_span.page_from}
                    onChange={(e) => handleChunkSourceSpanChange(chunkIndex, 'page_from', e.target.value)}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Source Span - Page To</label>
                  <input
                    type="number"
                    value={chunk.source_span.page_to}
                    onChange={(e) => handleChunkSourceSpanChange(chunkIndex, 'page_to', e.target.value)}
                    className="form-input"
                  />
                </div>
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={() => {
              setFormData(prev => ({
                ...prev,
                chunks: [...prev.chunks, {
                  chunk_id: '',
                  parent_doc_id: prev.doc_id,
                  chunk_index: prev.chunks.length,
                  hierarchy_path: '',
                  section_title: '',
                  previous_chunk_id: '',
                  next_chunk_id: '',
                  token_count: '',
                  language: '',
                  effective_classification: '',
                  effective_sensitivity_reason: '',
                  keywords: [],
                  summary: '',
                  cross_refs: [],
                  source_span: { start: '', end: '', page_from: '', page_to: '' }
                }]
              }));
            }}
            className="btn-add"
            style={{ width: '100%', marginTop: '20px' }}
          >
            + Add Another Chunk
          </button>
        </div>

        {/* Processing / Pipeline Metadata */}
        <div className={`form-section ${activeTab === 'processing' ? 'active' : ''}`}>
          <h2 className="section-title">Processing / Pipeline Metadata</h2>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">
                Ingestion Run ID <span className="required">*</span>
              </label>
              <input
                type="text"
                name="ingestion_run_id"
                value={formData.ingestion_run_id}
                onChange={handleChange}
                placeholder="ing_2025-11-20_001"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Chunking Strategy - Type</label>
              <select
                value={formData.chunking_strategy.type}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  chunking_strategy: { ...prev.chunking_strategy, type: e.target.value }
                }))}
                className="form-select"
              >
                <option value="">Select type</option>
                <option value="semantic">Semantic</option>
                <option value="recursive">Recursive</option>
                <option value="window">Window</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Chunking Strategy - Version</label>
              <input
                type="text"
                value={formData.chunking_strategy.version}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  chunking_strategy: { ...prev.chunking_strategy, version: e.target.value }
                }))}
                placeholder="kb-chunk-v1.2"
                className="form-input"
              />
            </div>
          </div>
        </div>

        {/* Retrieval / Embedding Metadata */}
        <div className={`form-section ${activeTab === 'embedding' ? 'active' : ''}`}>
          <h2 className="section-title">Retrieval / Embedding Metadata</h2>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">
                Embedding Model <span className="required">*</span>
              </label>
              <input
                type="text"
                name="embedding_model"
                value={formData.embedding_model}
                onChange={handleChange}
                placeholder="text-embedding-3-large@v2025-10"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Embedding Version <span className="required">*</span>
              </label>
              <input
                type="text"
                name="embedding_version"
                value={formData.embedding_version}
                onChange={handleChange}
                placeholder="kb-embed-v2.3"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Embedding Vector ID</label>
              <input
                type="text"
                name="embedding_vector_id"
                value={formData.embedding_vector_id}
                onChange={handleChange}
                placeholder="vec_0001"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Embedding Created At</label>
              <input
                type="datetime-local"
                name="embedding_created_at"
                value={formData.embedding_created_at}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Index Name</label>
              <input
                type="text"
                name="index_name"
                value={formData.index_name}
                onChange={handleChange}
                placeholder="kb_hr_index"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Similarity Score Last</label>
              <input
                type="number"
                step="0.01"
                name="similarity_score_last"
                value={formData.similarity_score_last}
                onChange={handleChange}
                placeholder="0.89"
                className="form-input"
              />
            </div>
          </div>
        </div>

        {/* Security / Compliance / Audit */}
        <div className={`form-section ${activeTab === 'security' ? 'active' : ''}`}>
          <h2 className="section-title">Security / Compliance / Audit</h2>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">PII Detected</label>
              <div className="checkbox-group">
                <input
                  type="checkbox"
                  name="pii_detected"
                  checked={formData.pii_detected}
                  onChange={handleChange}
                />
                <span>PII detected in document</span>
              </div>
            </div>

            {renderArrayInput('regulatory_tags', 'Regulatory Tags', 'e.g., GDPR, SOX', false)}
            {renderArrayInput('rbac_tags', 'RBAC Tags', 'e.g., role:HR-Manager', false)}

            <div className="form-group">
              <label className="form-label">Access Log Reference</label>
              <input
                type="text"
                name="access_log_ref"
                value={formData.access_log_ref}
                onChange={handleChange}
                placeholder="audit://kb/access/hr/..."
                className="form-input"
              />
            </div>
          </div>
        </div>

        {/* Lifecycle & Versioning */}
        <div className={`form-section ${activeTab === 'lifecycle' ? 'active' : ''}`}>
          <h2 className="section-title">Lifecycle & Versioning</h2>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">
                Status <span className="required">*</span>
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className={`form-select ${errors.status ? 'error' : ''}`}
              >
                <option value="active">Active</option>
                <option value="staged">Staged</option>
                <option value="archived">Archived</option>
                <option value="deleted">Deleted</option>
              </select>
              {errors.status && <div className="error-message">{errors.status}</div>}
            </div>

            <div className="form-group">
              <label className="form-label">Expiry Date</label>
              <input
                type="date"
                name="expiry_date"
                value={formData.expiry_date}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Review Cycle</label>
              <input
                type="text"
                name="review_cycle"
                value={formData.review_cycle}
                onChange={handleChange}
                placeholder="annual"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Last Reviewed At</label>
              <input
                type="datetime-local"
                name="last_reviewed_at"
                value={formData.last_reviewed_at}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Supersedes</label>
              <input
                type="text"
                name="supersedes"
                value={formData.supersedes}
                onChange={handleChange}
                placeholder="doc_1a2b3c"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Superseded By</label>
              <input
                type="text"
                name="superseded_by"
                value={formData.superseded_by}
                onChange={handleChange}
                placeholder="doc_1a2b3c"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Soft Delete At</label>
              <input
                type="datetime-local"
                name="soft_delete_at"
                value={formData.soft_delete_at}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Hard Delete At</label>
              <input
                type="datetime-local"
                name="hard_delete_at"
                value={formData.hard_delete_at}
                onChange={handleChange}
                className="form-input"
              />
            </div>
          </div>
        </div>

        {/* Optional Enrichment */}
        <div className={`form-section ${activeTab === 'enrichment' ? 'active' : ''}`}>
          <h2 className="section-title">Optional Enrichment</h2>
          <div className="form-grid">
            {renderArrayInput('topic_modeling', 'Topic Modeling', 'e.g., leave policy', false)}
            
            <div className="form-group full-width">
              <label className="form-label">Glossary Links</label>
              <div className="help-text">Format: term|glossary_id (e.g., FTE|g_102)</div>
              {formData.glossary_links.map((link, idx) => (
                <div key={idx} className="array-item">
                  <input
                    type="text"
                    value={typeof link === 'string' ? link : `${link.term}|${link.glossary_id}`}
                    onChange={(e) => {
                      const [term, glossary_id] = e.target.value.split('|');
                      const newLinks = [...formData.glossary_links];
                      newLinks[idx] = { term: term || '', glossary_id: glossary_id || '' };
                      setFormData(prev => ({ ...prev, glossary_links: newLinks }));
                    }}
                    placeholder="FTE|g_102"
                    className="form-input"
                  />
                  <button
                    type="button"
                    onClick={() => removeArrayItem('glossary_links', idx)}
                    className="btn-remove"
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => addArrayItem('glossary_links', { term: '', glossary_id: '' })}
                className="btn-add"
              >
                + Add Glossary Link
              </button>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="button" onClick={handleReset} className="btn-reset">
            Reset Form
          </button>
          <button type="submit" className="btn-submit">
            Submit Document
          </button>
        </div>
      </form>

      {submitResult && (
        <div className={`submit-result ${submitResult.type === 'error' ? 'error' : ''}`}>
          <h3>{submitResult.message}</h3>
          {submitResult.data && (
            <pre>{JSON.stringify(submitResult.data, null, 2)}</pre>
          )}
        </div>
      )}
    </div>
  );
};

export default DocumentForm;

